import { useEffect, useMemo, useRef, useState } from 'react';
import type { Clip, Track } from '@cinegrid/core';
import { suggestTrackName } from '@cinegrid/core';
import { getLivePlayhead } from '../../engine/livePlayhead';
import { formatTimecode, findGapsOnTrack, projectDuration } from '../../timeline/ops';
import { useEditorStore } from '../../state/editorStore';
import {
  AddAudioLayerDialog,
  AddVideoLayerDialog,
} from '../controls/AppDialog';
import { PanelShell } from '../controls/PanelShell';
import { ClipFilmstrip, ClipWaveform } from './ClipMediaPreview';
import { KeyGraphPanel } from './KeyGraphPanel';
import { TimelineAudioMeter } from './TimelineAudioMeter';

const HEADER_W = 88;
const RULER_H = 24;

type BottomTab = 'timeline' | 'keygraph';

function clipFitsTrack(clip: Clip, track: Track): boolean {
  if (track.locked) return false;
  if (clip.kind === 'audio') return track.kind === 'audio';
  // video, text, adjustment, solid → video tracks
  return track.kind === 'video';
}

function trackKindLabel(track: Track): string {
  if (track.kind === 'audio') {
    const ch = track.channels === 'mono' ? 'Mono' : 'Stereo';
    return `Audio · ${ch}`;
  }
  return track.kind.charAt(0).toUpperCase() + track.kind.slice(1);
}

export function TimelinePanel() {
  const project = useEditorStore((s) => s.project);
  const playhead = useEditorStore((s) => s.playhead);
  const isPlaying = useEditorStore((s) => s.isPlaying);
  const setPlayhead = useEditorStore((s) => s.setPlayhead);
  const zoom = useEditorStore((s) => s.timelineZoom);
  const playheadElRef = useRef<HTMLDivElement>(null);
  const zoomMin = useEditorStore((s) => s.timelineZoomMin);
  const setTimelineZoom = useEditorStore((s) => s.setTimelineZoom);
  const setTimelineZoomMin = useEditorStore((s) => s.setTimelineZoomMin);
  const setTimelineScrollX = useEditorStore((s) => s.setTimelineScrollX);
  const selection = useEditorStore((s) => s.selection);
  const selectedGap = useEditorStore((s) => s.selectedGap);
  const selectClips = useEditorStore((s) => s.selectClips);
  const selectGap = useEditorStore((s) => s.selectGap);
  const tool = useEditorStore((s) => s.tool);
  const cutAtPlayhead = useEditorStore((s) => s.cutAtPlayhead);
  const moveClips = useEditorStore((s) => s.moveClips);
  const addClipFromAsset = useEditorStore((s) => s.addClipFromAsset);
  const addTextClip = useEditorStore((s) => s.addTextClip);
  const unlinkClip = useEditorStore((s) => s.unlinkClip);
  const linkSelectedAv = useEditorStore((s) => s.linkSelectedAv);
  const compositions = useEditorStore((s) => s.project.compositions ?? []);
  const activeCompositionId = useEditorStore((s) => s.project.activeCompositionId);
  const setActiveComposition = useEditorStore((s) => s.setActiveComposition);
  const createComposition = useEditorStore((s) => s.createComposition);
  const renameTrack = useEditorStore((s) => s.renameTrack);
  const setTrackHeight = useEditorStore((s) => s.setTrackHeight);
  const addVideoTrack = useEditorStore((s) => s.addVideoTrack);
  const addAudioTrack = useEditorStore((s) => s.addAudioTrack);
  const inPoint = useEditorStore((s) => s.inPoint);
  const outPoint = useEditorStore((s) => s.outPoint);
  const trimCompToWorkArea = useEditorStore((s) => s.trimCompToWorkArea);
  const snapEnabled = useEditorStore((s) => s.snapEnabled);
  const markers = project.markers ?? [];

  // Smooth playhead line during playback without 60fps React re-renders
  useEffect(() => {
    const el = playheadElRef.current;
    if (!el) return;
    if (!isPlaying) {
      el.style.left = `${HEADER_W + playhead * zoom}px`;
      return;
    }
    let raf = 0;
    const tick = () => {
      el.style.left = `${HEADER_W + getLivePlayhead() * zoom}px`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying, playhead, zoom]);

  const [ctxMenu, setCtxMenu] = useState<{
    x: number;
    y: number;
    clipId: string;
    linked: boolean;
    canLink: boolean;
  } | null>(null);
  const [trackMenu, setTrackMenu] = useState<{
    x: number;
    y: number;
    trackId: string;
  } | null>(null);
  const [rulerMenu, setRulerMenu] = useState<{ x: number; y: number } | null>(null);
  const [renamingTrackId, setRenamingTrackId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [addVideoDlg, setAddVideoDlg] = useState<{
    relativeToTrackId: string;
    initialName: string;
  } | null>(null);
  const [addAudioDlg, setAddAudioDlg] = useState<{
    relativeToTrackId: string;
    initialName: string;
  } | null>(null);
  const [bottomTab, setBottomTab] = useState<BottomTab>('timeline');
  const renameInputRef = useRef<HTMLInputElement>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const tracksBodyRef = useRef<HTMLDivElement>(null);
  const scrubRafRef = useRef<number>(0);
  const scrubTimeRef = useRef<number | null>(null);
  const markDragRef = useRef<'in' | 'out' | null>(null);
  const trimDragRef = useRef<{
    clipId: string;
    edge: 'in' | 'out';
    ripple: boolean;
  } | null>(null);
  const dragRef = useRef<{
    primaryId: string;
    startX: number;
    startY: number;
    /** Original starts for every clip being dragged */
    origins: { clipId: string; start: number; trackId: string }[];
    primaryOrigTrackId: string;
    moved: boolean;
    additive: boolean;
    wasSelected: boolean;
  } | null>(null);

  const duration = useMemo(() => projectDuration(project), [project]);
  const compDuration = Math.max(1, project.settings.duration);
  /** Content width: at least one viewport of the composition, no huge empty tail. */
  const [viewportW, setViewportW] = useState(960);
  const widthPx = Math.max(compDuration * zoom, Math.max(0, viewportW - HEADER_W));

  // Min zoom = fit full composition duration in the visible timeline lane
  useEffect(() => {
    if (bottomTab !== 'timeline') return;
    let ro: ResizeObserver | null = null;
    const id = requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el) return;
      const measure = () => {
        const w = el.clientWidth;
        setViewportW(w);
        const lane = Math.max(120, w - HEADER_W);
        setTimelineZoomMin(lane / compDuration);
      };
      measure();
      ro = new ResizeObserver(measure);
      ro.observe(el);
    });
    return () => {
      cancelAnimationFrame(id);
      ro?.disconnect();
    };
  }, [compDuration, setTimelineZoomMin, bottomTab]);

  const timeFromClientX = (clientX: number) => {
    const el = scrollRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left + el.scrollLeft - HEADER_W;
    let t = x / zoom;
    if (snapEnabled) {
      const frame = 1 / project.settings.frameRate;
      t = Math.round(t / frame) * frame;
    }
    return Math.max(0, t);
  };

  useEffect(() => {
    if (!renamingTrackId) return;
    const t = window.setTimeout(() => {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(t);
  }, [renamingTrackId]);

  useEffect(() => {
    if (!trackMenu && !ctxMenu && !rulerMenu) return;
    const close = () => {
      setTrackMenu(null);
      setCtxMenu(null);
      setRulerMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [trackMenu, ctxMenu, rulerMenu]);

  const commitRename = () => {
    if (!renamingTrackId) return;
    const trimmed = renameDraft.trim();
    if (trimmed) renameTrack(renamingTrackId, trimmed);
    setRenamingTrackId(null);
  };

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const trim = trimDragRef.current;
      if (trim) {
        const el = scrollRef.current;
        if (!el) return;
        const state = useEditorStore.getState();
        const z = state.timelineZoom;
        const rect = el.getBoundingClientRect();
        let t = (e.clientX - rect.left + el.scrollLeft - HEADER_W) / z;
        if (state.snapEnabled) {
          const frame = 1 / state.project.settings.frameRate;
          t = Math.round(t / frame) * frame;
        }
        state.trimClipAt(trim.clipId, trim.edge, Math.max(0, t), trim.ripple);
        return;
      }

      const which = markDragRef.current;
      if (!which) return;
      const el = scrollRef.current;
      if (!el) return;
      const state = useEditorStore.getState();
      const z = state.timelineZoom;
      const rect = el.getBoundingClientRect();
      let t = (e.clientX - rect.left + el.scrollLeft - HEADER_W) / z;
      if (state.snapEnabled) {
        const frame = 1 / state.project.settings.frameRate;
        t = Math.round(t / frame) * frame;
      }
      t = Math.max(0, t);
      const frame = 1 / state.project.settings.frameRate;
      if (which === 'in') {
        const out = state.outPoint;
        state.setInPoint(out != null ? Math.min(t, out - frame) : t);
      } else {
        const inn = state.inPoint;
        state.setOutPoint(inn != null ? Math.max(t, inn + frame) : t);
      }
    };

    const onUp = () => {
      trimDragRef.current = null;
      markDragRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const state = useEditorStore.getState();
      const primary = state.project.clips.find((c) => c.id === d.primaryId);
      if (!primary) return;

      const dist = Math.hypot(e.clientX - d.startX, e.clientY - d.startY);
      if (dist > 3 && !d.moved) {
        d.moved = true;
        document.body.style.cursor = 'grabbing';
      }
      if (!d.moved) return;

      const z = state.timelineZoom;
      const dx = e.clientX - d.startX;
      let deltaT = dx / z;
      if (state.snapEnabled) {
        const frame = 1 / state.project.settings.frameRate;
        const primaryStart = d.origins.find((o) => o.clipId === d.primaryId)?.start ?? primary.start;
        const snapped = Math.round((primaryStart + deltaT) / frame) * frame;
        deltaT = snapped - primaryStart;
      }

      // Track change for the dragged clip only (linked A/V partners stay on their tracks)
      let primaryTrackId = d.primaryOrigTrackId;
      {
        const body = tracksBodyRef.current;
        if (body) {
          const rows = body.querySelectorAll<HTMLElement>('[data-track-id]');
          let hit: string | null = null;
          for (const row of rows) {
            const rect = row.getBoundingClientRect();
            if (e.clientY >= rect.top && e.clientY < rect.bottom) {
              const id = row.dataset.trackId!;
              const track = state.project.tracks.find((t) => t.id === id);
              if (track && clipFitsTrack(primary, track)) hit = id;
              break;
            }
          }
          if (!hit) {
            let best: { id: string; dist: number } | null = null;
            for (const row of rows) {
              const id = row.dataset.trackId!;
              const track = state.project.tracks.find((t) => t.id === id);
              if (!track || !clipFitsTrack(primary, track)) continue;
              const rect = row.getBoundingClientRect();
              const mid = (rect.top + rect.bottom) / 2;
              const distY = Math.abs(e.clientY - mid);
              if (!best || distY < best.dist) best = { id, dist: distY };
            }
            hit = best?.id ?? null;
          }
          if (hit) primaryTrackId = hit;
        }
      }

      state.moveClips(
        d.origins.map((o) => ({
          clipId: o.clipId,
          start: Math.max(0, o.start + deltaT),
          trackId: o.clipId === d.primaryId ? primaryTrackId : o.trackId,
        }))
      );
    };

    const onUp = () => {
      const d = dragRef.current;
      if (d && !d.moved && !d.additive) {
        // Plain click on a clip that was already in a multi-selection → exclusive select
        if (d.wasSelected) {
          useEditorStore.getState().selectClips([d.primaryId], 'replace');
        }
      }
      dragRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [ctxMenu]);

  /** One playhead update per animation frame while scrubbing the ruler. */
  const scrubToClientX = (clientX: number) => {
    useEditorStore.getState().setPlaying(false);
    scrubTimeRef.current = timeFromClientX(clientX);
    if (scrubRafRef.current) return;
    scrubRafRef.current = requestAnimationFrame(() => {
      scrubRafRef.current = 0;
      const t = scrubTimeRef.current;
      if (t != null) setPlayhead(t);
    });
  };

  const onRulerPointer = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    scrubToClientX(e.clientX);
  };

  const rulerMarks = useMemo(() => {
    const step =
      zoom > 200 ? 0.5 : zoom > 100 ? 1 : zoom > 40 ? 5 : zoom > 20 ? 10 : 30;
    const marks: number[] = [];
    for (let t = 0; t <= duration + step; t += step) marks.push(t);
    return marks;
  }, [duration, zoom]);

  /** Zoom centered on the green playhead. */
  const zoomAroundPlayhead = (nextZoom: number) => {
    const z1 = Math.min(800, Math.max(zoomMin, nextZoom));
    setTimelineZoom(z1);
    const el = scrollRef.current;
    if (!el) return;
    const target = HEADER_W + playhead * z1 - el.clientWidth * 0.45;
    el.scrollLeft = Math.max(0, target);
    setTimelineScrollX(el.scrollLeft);
  };

  useEffect(() => {
    if (bottomTab !== 'timeline') return;
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.altKey || e.metaKey)) return;
      e.preventDefault();
      const { timelineZoom, timelineZoomMin } = useEditorStore.getState();
      const z1 = Math.min(
        800,
        Math.max(timelineZoomMin, timelineZoom * (e.deltaY > 0 ? 0.9 : 1.1))
      );
      setTimelineZoom(z1);
      const ph = useEditorStore.getState().playhead;
      const target = HEADER_W + ph * z1 - el.clientWidth * 0.45;
      el.scrollLeft = Math.max(0, target);
      setTimelineScrollX(el.scrollLeft);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [bottomTab, setTimelineZoom, setTimelineScrollX]);

  return (
    <PanelShell
      bodyClassName="overflow-hidden"
      title={
        <div className="flex h-7 items-stretch -mx-2">
          {(
            [
              { id: 'timeline' as const, label: 'Timeline' },
              { id: 'keygraph' as const, label: 'Key Graph' },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setBottomTab(t.id)}
              className={`px-2.5 text-2xs font-semibold uppercase tracking-wider ${
                bottomTab === t.id
                  ? 'border-b-2 border-accent text-accent'
                  : 'text-text-low hover:text-text-high'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      }
      actions={
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            title="Zoom out"
            className="px-1 text-2xs text-text-low hover:text-text-high"
            onClick={() => zoomAroundPlayhead(zoom / 1.25)}
          >
            −
          </button>
          <input
            type="range"
            min={Math.round(zoomMin)}
            max={800}
            step={1}
            value={Math.round(zoom)}
            title="Zoom (centered on playhead) — min fits composition duration"
            aria-label="Timeline zoom"
            className="h-1 w-20 cursor-default accent-[var(--cg-accent)]"
            onChange={(e) => zoomAroundPlayhead(Number(e.target.value))}
          />
          <button
            type="button"
            title="Zoom in"
            className="px-1 text-2xs text-text-low hover:text-text-high"
            onClick={() => zoomAroundPlayhead(zoom * 1.25)}
          >
            +
          </button>
          <span className="timecode min-w-[3.5rem] text-right text-2xs text-text-low">
            {zoom.toFixed(0)} px/s
          </span>
        </div>
      }
    >
      {bottomTab === 'keygraph' ? (
        <KeyGraphPanel />
      ) : (
      <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-7 shrink-0 items-stretch border-b border-hairline bg-panel">
        <div className="flex min-w-0 flex-1 items-stretch overflow-x-auto">
          {compositions.map((comp) => {
            const active = comp.id === activeCompositionId;
            return (
              <button
                key={comp.id}
                type="button"
                title={comp.name}
                onClick={() => setActiveComposition(comp.id)}
                className={`max-w-[140px] shrink-0 truncate border-r border-hairline px-2.5 text-2xs ${
                  active
                    ? 'bg-raised text-accent'
                    : 'text-text-low hover:bg-raised/60 hover:text-text-high'
                }`}
              >
                {comp.name}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          title="New composition"
          className="shrink-0 border-l border-hairline px-2 text-2xs text-text-low hover:text-accent"
          onClick={() => createComposition()}
        >
          +
        </button>
      </div>
      <div className="flex min-h-0 flex-1">
      <div
        ref={scrollRef}
        className="min-h-0 min-w-0 flex-1 overflow-auto"
        onScroll={(e) => setTimelineScrollX((e.target as HTMLDivElement).scrollLeft)}
      >
        <div style={{ width: widthPx + HEADER_W, minHeight: '100%' }} className="relative">
          {/* Ruler */}
          <div
            className="sticky top-0 z-20 flex border-b border-hairline bg-panel"
            style={{ height: RULER_H }}
            onPointerDown={onRulerPointer}
            onPointerMove={(e) => {
              if (e.buttons) scrubToClientX(e.clientX);
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCtxMenu(null);
              setTrackMenu(null);
              setRulerMenu({ x: e.clientX, y: e.clientY });
            }}
          >
            <div className="sticky left-0 z-30 w-[88px] shrink-0 border-r border-hairline bg-panel" />
            <div className="relative flex-1">
              {rulerMarks.map((t) => (
                <div
                  key={t}
                  className="absolute top-0 h-full border-l border-hairline"
                  style={{ left: t * zoom }}
                >
                  <span className="timecode ml-1 text-2xs text-text-low">
                    {formatTimecode(t, project.settings.frameRate).slice(0, 8)}
                  </span>
                </div>
              ))}
              {inPoint != null && outPoint != null && outPoint > inPoint && (
                <div
                  className="pointer-events-none absolute top-0 h-full bg-accent/15"
                  style={{
                    left: inPoint * zoom,
                    width: (outPoint - inPoint) * zoom,
                  }}
                />
              )}
              {inPoint != null && (
                <div
                  role="slider"
                  aria-label="Mark In"
                  title={`In ${formatTimecode(inPoint, project.settings.frameRate)} — drag to move`}
                  className="absolute top-0 z-20 h-full w-3 -translate-x-1/2 cursor-ew-resize"
                  style={{ left: inPoint * zoom }}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    markDragRef.current = 'in';
                    document.body.style.cursor = 'ew-resize';
                    document.body.style.userSelect = 'none';
                  }}
                >
                  <div className="absolute left-1/2 top-0 h-full w-0.5 -translate-x-1/2 bg-accent" />
                  <span className="absolute left-1/2 top-0 -translate-x-1/2 bg-accent px-0.5 text-[9px] font-bold leading-none text-base">
                    I
                  </span>
                </div>
              )}
              {outPoint != null && (
                <div
                  role="slider"
                  aria-label="Mark Out"
                  title={`Out ${formatTimecode(outPoint, project.settings.frameRate)} — drag to move`}
                  className="absolute top-0 z-20 h-full w-3 -translate-x-1/2 cursor-ew-resize"
                  style={{ left: outPoint * zoom }}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    markDragRef.current = 'out';
                    document.body.style.cursor = 'ew-resize';
                    document.body.style.userSelect = 'none';
                  }}
                >
                  <div className="absolute left-1/2 top-0 h-full w-0.5 -translate-x-1/2 bg-accent" />
                  <span className="absolute left-1/2 top-0 -translate-x-1/2 bg-accent px-0.5 text-[9px] font-bold leading-none text-base">
                    O
                  </span>
                </div>
              )}
              {markers.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  title={`${m.label} @ ${formatTimecode(m.time, project.settings.frameRate)}`}
                  className="absolute top-0 z-10 h-full w-0 border-0 bg-transparent p-0"
                  style={{ left: m.time * zoom }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPlayhead(m.time);
                  }}
                >
                  <span
                    className="absolute left-1/2 top-0 block h-0 w-0 -translate-x-1/2 border-l-[5px] border-r-[5px] border-t-[8px] border-l-transparent border-r-transparent"
                    style={{ borderTopColor: m.color }}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Tracks */}
          <div ref={tracksBodyRef}>
            {project.tracks.map((track) => (
              <div
                key={track.id}
                data-track-id={track.id}
                className="relative flex border-b border-hairline"
                style={{ height: track.height }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'copy';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const assetId = e.dataTransfer.getData('application/cinegrid-asset');
                  const t = timeFromClientX(e.clientX);
                  if (assetId) {
                    const asset = project.assets.find((a) => a.id === assetId);
                    if (!asset) return;
                    const ok =
                      asset.kind === 'audio'
                        ? track.kind === 'audio'
                        : track.kind === 'video';
                    if (ok) addClipFromAsset(assetId, track.id, t);
                  }
                }}
                onDoubleClick={(e) => {
                  if (tool === 'text' && track.kind === 'video') {
                    addTextClip(track.id, timeFromClientX(e.clientX));
                  }
                }}
                onPointerDown={(e) => {
                  // Click empty lane: select gap if any, else clear
                  const t = (e.target as HTMLElement).dataset.trackLane
                    ? timeFromClientX(e.clientX)
                    : e.target === e.currentTarget
                      ? timeFromClientX(e.clientX)
                      : null;
                  if (t == null) return;
                  if (e.shiftKey || e.metaKey || e.ctrlKey) return;
                  const gaps = findGapsOnTrack(project, track.id);
                  const gap = gaps.find((g) => t >= g.start && t < g.end);
                  if (gap) {
                    e.stopPropagation();
                    selectGap(gap);
                  } else {
                    selectClips([], 'replace');
                    selectGap(null);
                  }
                }}
              >
                <div
                  className="sticky left-0 z-10 flex w-[88px] shrink-0 flex-col justify-center border-r border-hairline bg-panel px-2"
                  data-track-header
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCtxMenu(null);
                    setTrackMenu({
                      x: e.clientX,
                      y: e.clientY,
                      trackId: track.id,
                    });
                  }}
                >
                  {renamingTrackId === track.id ? (
                    <input
                      ref={renameInputRef}
                      type="text"
                      value={renameDraft}
                      className="w-full border border-accent bg-base px-0.5 py-0 text-2xs font-semibold text-text-high outline-none"
                      onChange={(e) => setRenameDraft(e.target.value)}
                      onBlur={commitRename}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitRename();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setRenamingTrackId(null);
                        }
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <button
                      type="button"
                      className="truncate text-left text-2xs font-semibold text-text-high hover:text-accent"
                      title="Double-click to rename"
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setRenameDraft(track.name);
                        setRenamingTrackId(track.id);
                      }}
                    >
                      {track.name}
                    </button>
                  )}
                  <span className="truncate text-2xs text-text-low">
                    {trackKindLabel(track)}
                  </span>
                </div>
                <div className="relative flex-1" data-track-lane style={{ width: widthPx }}>
                  {findGapsOnTrack(project, track.id).map((gap) => {
                    const selected =
                      selectedGap?.trackId === gap.trackId &&
                      Math.abs(selectedGap.start - gap.start) < 0.001 &&
                      Math.abs(selectedGap.end - gap.end) < 0.001;
                    const w = (gap.end - gap.start) * zoom;
                    if (w < 2) return null;
                    return (
                      <button
                        key={`gap-${gap.trackId}-${gap.start}`}
                        type="button"
                        title={`Gap ${formatTimecode(gap.end - gap.start, project.settings.frameRate)} — Delete to close`}
                        className={`absolute top-1 bottom-1 z-0 border border-dashed ${
                          selected
                            ? 'border-[var(--cg-border-strong)] bg-[color-mix(in_srgb,var(--cg-text-low)_35%,transparent)]'
                            : 'border-hairline/80 bg-transparent hover:bg-[color-mix(in_srgb,var(--cg-text-low)_12%,transparent)]'
                        }`}
                        style={{ left: gap.start * zoom, width: w }}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          selectGap(gap);
                        }}
                      />
                    );
                  })}
                  {project.clips
                    .filter((c) => c.trackId === track.id)
                    .map((clip) => {
                      const selected = selection.includes(clip.id);
                      const linked = !!clip.linkGroupId;
                      const widthPx = Math.max(8, clip.duration * zoom);
                      const asset = clip.assetId
                        ? project.assets.find((a) => a.id === clip.assetId)
                        : undefined;
                      const showFilm =
                        clip.kind === 'video' &&
                        !!asset &&
                        (asset.kind === 'video' || asset.kind === 'image');
                      const showWave =
                        clip.kind === 'audio' &&
                        !!asset &&
                        (asset.kind === 'audio' || asset.kind === 'video');
                      const bg =
                        clip.kind === 'audio'
                          ? 'var(--cg-clip-audio)'
                          : clip.kind === 'text'
                            ? 'var(--cg-clip-text)'
                            : clip.kind === 'adjustment'
                              ? '#2a3540'
                              : clip.kind === 'solid'
                                ? clip.properties.solidColor ||
                                  (clip.assetId
                                    ? project.assets.find((a) => a.id === clip.assetId)
                                        ?.solidColor
                                    : undefined) ||
                                  '#808080'
                                : 'var(--cg-clip-video)';
                      return (
                        <div
                          key={clip.id}
                          className={`group/clip absolute top-1 bottom-1 z-[1] overflow-hidden border text-2xs ${
                            selected ? 'border-accent ring-1 ring-accent/40' : 'border-hairline'
                          }`}
                          style={{
                            left: clip.start * zoom,
                            width: widthPx,
                            background: bg,
                            cursor: tool === 'blade' ? 'cell' : 'default',
                            touchAction: 'none',
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const state = useEditorStore.getState();
                            let ids = state.selection;
                            if (!ids.includes(clip.id)) {
                              state.selectClips([clip.id], 'replace');
                              ids = [clip.id];
                            }
                            const selected = ids
                              .map((id) => state.project.clips.find((c) => c.id === id))
                              .filter((c): c is Clip => !!c);
                            const hasVideo = selected.some((c) => c.kind === 'video');
                            const hasAudio = selected.some((c) => c.kind === 'audio');
                            const alreadyLinked =
                              !!clip.linkGroupId &&
                              selected.some(
                                (c) =>
                                  c.id !== clip.id &&
                                  c.linkGroupId === clip.linkGroupId
                              );
                            setCtxMenu({
                              x: e.clientX,
                              y: e.clientY,
                              clipId: clip.id,
                              linked: !!clip.linkGroupId,
                              canLink: hasVideo && hasAudio && !alreadyLinked,
                            });
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (tool === 'blade') {
                              selectClips([clip.id], 'replace');
                              setPlayhead(
                                Math.min(
                                  clip.start + clip.duration - 0.01,
                                  Math.max(clip.start + 0.01, timeFromClientX(e.clientX))
                                )
                              );
                              cutAtPlayhead();
                            }
                          }}
                          onPointerDown={(e) => {
                            if (tool === 'blade') return;
                            if (tool !== 'select' && tool !== 'ripple' && tool !== 'hand') return;
                            if (e.button !== 0) return;
                            // Edge hit → trim instead of move
                            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            const edgePx = 8;
                            const nearIn = e.clientX - rect.left <= edgePx;
                            const nearOut = rect.right - e.clientX <= edgePx;
                            if (nearIn || nearOut) {
                              e.stopPropagation();
                              e.preventDefault();
                              selectClips([clip.id], 'replace');
                              trimDragRef.current = {
                                clipId: clip.id,
                                edge: nearIn ? 'in' : 'out',
                                ripple: tool === 'ripple',
                              };
                              document.body.style.cursor = 'ew-resize';
                              document.body.style.userSelect = 'none';
                              return;
                            }

                            e.stopPropagation();
                            e.preventDefault();

                            const shift = e.shiftKey;
                            const toggle = e.metaKey || e.ctrlKey;
                            const additive = shift || toggle;
                            const wasSelected = selection.includes(clip.id);
                            const state = useEditorStore.getState();

                            if (shift) {
                              state.selectClips([clip.id], 'add');
                            } else if (toggle) {
                              state.selectClips([clip.id], 'toggle');
                            } else if (!wasSelected) {
                              state.selectClips([clip.id], 'replace');
                            }

                            const sel = useEditorStore.getState().selection;
                            const ids = sel.includes(clip.id) ? sel : [clip.id];
                            const origins = ids
                              .map((id) => {
                                const c = useEditorStore
                                  .getState()
                                  .project.clips.find((x) => x.id === id);
                                return c
                                  ? { clipId: c.id, start: c.start, trackId: c.trackId }
                                  : null;
                              })
                              .filter(Boolean) as {
                              clipId: string;
                              start: number;
                              trackId: string;
                            }[];

                            dragRef.current = {
                              primaryId: clip.id,
                              startX: e.clientX,
                              startY: e.clientY,
                              origins,
                              primaryOrigTrackId: track.id,
                              moved: false,
                              additive,
                              wasSelected,
                            };
                            document.body.style.cursor = 'grab';
                            document.body.style.userSelect = 'none';
                          }}
                        >
                          {showFilm && asset && (
                            <ClipFilmstrip clip={clip} asset={asset} widthPx={widthPx} />
                          )}
                          {showWave && asset && (
                            <ClipWaveform clip={clip} asset={asset} widthPx={widthPx} />
                          )}
                          <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-black/50 via-transparent to-black/20" />
                          <div className="pointer-events-none relative z-[2] flex h-full items-start gap-1 overflow-hidden px-1 pt-0.5">
                            {linked && (
                              <span
                                className="shrink-0 text-[10px] text-accent drop-shadow"
                                title="Linked A/V — right-click to unlink"
                              >
                                ⛓
                              </span>
                            )}
                            <span className="truncate font-medium text-text-high drop-shadow">
                              {clip.name}
                            </span>
                          </div>
                          {/* Trim handles */}
                          <div
                            className={`absolute bottom-0 left-0 top-0 z-20 w-2 cursor-ew-resize ${
                              selected ? 'bg-accent/50' : 'bg-transparent group-hover/clip:bg-accent/30'
                            }`}
                            title="Trim in (drag)"
                            onPointerDown={(e) => {
                              if (tool === 'blade') return;
                              e.stopPropagation();
                              e.preventDefault();
                              selectClips([clip.id], 'replace');
                              trimDragRef.current = {
                                clipId: clip.id,
                                edge: 'in',
                                ripple: tool === 'ripple',
                              };
                              document.body.style.cursor = 'ew-resize';
                              document.body.style.userSelect = 'none';
                            }}
                          />
                          <div
                            className={`absolute bottom-0 right-0 top-0 z-20 w-2 cursor-ew-resize ${
                              selected ? 'bg-accent/50' : 'bg-transparent group-hover/clip:bg-accent/30'
                            }`}
                            title="Trim out (drag)"
                            onPointerDown={(e) => {
                              if (tool === 'blade') return;
                              e.stopPropagation();
                              e.preventDefault();
                              selectClips([clip.id], 'replace');
                              trimDragRef.current = {
                                clipId: clip.id,
                                edge: 'out',
                                ripple: tool === 'ripple',
                              };
                              document.body.style.cursor = 'ew-resize';
                              document.body.style.userSelect = 'none';
                            }}
                          />
                          {clip.transitionOut && (
                            <div
                              className="pointer-events-none absolute bottom-0 right-0 top-0 bg-accent/30"
                              style={{ width: clip.transitionOut.duration * zoom }}
                              title="Cross dissolve"
                            />
                          )}
                        </div>
                      );
                    })}
                </div>
                <div
                  role="separator"
                  aria-orientation="horizontal"
                  aria-label={`Resize ${track.name}`}
                  title="Drag to resize track height"
                  className="absolute inset-x-0 bottom-0 z-40 h-1.5 cursor-ns-resize hover:bg-accent/35 active:bg-accent/50"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const startY = e.clientY;
                    const startH = track.height;
                    const target = e.currentTarget;
                    target.setPointerCapture(e.pointerId);
                    document.body.style.cursor = 'ns-resize';
                    document.body.style.userSelect = 'none';

                    const onMove = (ev: PointerEvent) => {
                      setTrackHeight(track.id, startH + (ev.clientY - startY));
                    };
                    const onUp = (ev: PointerEvent) => {
                      try {
                        target.releasePointerCapture(ev.pointerId);
                      } catch {
                        /* ignore */
                      }
                      window.removeEventListener('pointermove', onMove);
                      window.removeEventListener('pointerup', onUp);
                      document.body.style.cursor = '';
                      document.body.style.userSelect = '';
                    };
                    window.addEventListener('pointermove', onMove);
                    window.addEventListener('pointerup', onUp);
                  }}
                />
              </div>
            ))}
          </div>

          {/* Playhead */}
          <div
            ref={playheadElRef}
            className="pointer-events-none absolute bottom-0 top-0 z-30 w-px bg-playhead"
            style={{ left: HEADER_W + playhead * zoom }}
          >
            <div className="absolute -left-1.5 top-0 h-0 w-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-playhead" />
          </div>
        </div>
      </div>
      <TimelineAudioMeter />
      </div>
      </div>
      )}
      {ctxMenu && (
        <div
          className="fixed z-[100] min-w-[160px] border border-hairline bg-panel py-1 shadow-none"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {ctxMenu.linked && (
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent"
              onClick={() => {
                unlinkClip(ctxMenu.clipId);
                setCtxMenu(null);
              }}
            >
              Unlink audio / video
            </button>
          )}
          {ctxMenu.canLink && (
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent"
              onClick={() => {
                linkSelectedAv();
                setCtxMenu(null);
              }}
            >
              Link audio / video
            </button>
          )}
          {!ctxMenu.linked && !ctxMenu.canLink && (
            <div className="px-3 py-1.5 text-2xs text-text-low">
              Select a video + audio clip to link
            </div>
          )}
        </div>
      )}

      {trackMenu && (
        <div
          className="fixed z-[100] min-w-[168px] border border-hairline bg-panel py-1"
          style={{ left: trackMenu.x, top: trackMenu.y }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent"
            onClick={() => {
              const track = project.tracks.find((t) => t.id === trackMenu.trackId);
              if (track) {
                setRenameDraft(track.name);
                setRenamingTrackId(track.id);
              }
              setTrackMenu(null);
            }}
          >
            Rename Layer…
          </button>
          <button
            type="button"
            className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent"
            onClick={() => {
              setAddVideoDlg({
                relativeToTrackId: trackMenu.trackId,
                initialName: suggestTrackName(project.tracks, 'video'),
              });
              setTrackMenu(null);
            }}
          >
            Add Video Layer…
          </button>
          <button
            type="button"
            className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent"
            onClick={() => {
              setAddAudioDlg({
                relativeToTrackId: trackMenu.trackId,
                initialName: suggestTrackName(project.tracks, 'audio'),
              });
              setTrackMenu(null);
            }}
          >
            Add Audio Layer…
          </button>
        </div>
      )}

      {rulerMenu && (
        <div
          className="fixed z-[100] min-w-[180px] border border-hairline bg-panel py-1"
          style={{ left: rulerMenu.x, top: rulerMenu.y }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            disabled={
              inPoint == null || outPoint == null || outPoint <= inPoint
            }
            title={
              inPoint == null || outPoint == null || outPoint <= inPoint
                ? 'Set In (I) and Out (O) marks first'
                : `Set duration to ${formatTimecode(
                    outPoint! - inPoint!,
                    project.settings.frameRate
                  )} and shift content`
            }
            className="block w-full px-3 py-1.5 text-left text-2xs text-text-high hover:bg-raised hover:text-accent disabled:opacity-35 disabled:hover:bg-transparent disabled:hover:text-text-high"
            onClick={() => {
              trimCompToWorkArea();
              setRulerMenu(null);
            }}
          >
            Trim Comp to Work Area
          </button>
        </div>
      )}

      <AddVideoLayerDialog
        open={!!addVideoDlg}
        initialName={addVideoDlg?.initialName ?? 'V1'}
        onClose={() => setAddVideoDlg(null)}
        onSubmit={(name) => {
          if (addVideoDlg) addVideoTrack(name, addVideoDlg.relativeToTrackId);
          setAddVideoDlg(null);
        }}
      />

      <AddAudioLayerDialog
        open={!!addAudioDlg}
        initialName={addAudioDlg?.initialName ?? 'A1'}
        onClose={() => setAddAudioDlg(null)}
        onSubmit={(name, channels) => {
          if (addAudioDlg) addAudioTrack(name, channels, addAudioDlg.relativeToTrackId);
          setAddAudioDlg(null);
        }}
      />
    </PanelShell>
  );
}
